import { useState, useEffect, useCallback } from "react";
import { 
  DollarSign, Users, FileText, Calendar, Upload, Download,
  CheckCircle, Clock, CalendarClock, RefreshCw, Search, Filter,
  Eye, Percent, Building2, User, ChevronRight, X, AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axCfg = { withCredentials: true };

const STATUS_CONFIG = {
  pending: { label: "Pendiente de Pago", color: "bg-amber-100 text-amber-800", icon: Clock },
  scheduled: { label: "Programado", color: "bg-blue-100 text-blue-800", icon: CalendarClock },
  paid: { label: "Pagada", color: "bg-green-100 text-green-800", icon: CheckCircle }
};

export default function ComisionesTab() {
  const [loading, setLoading] = useState(true);
  const [commissions, setCommissions] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Filters
  const [filterAgent, setFilterAgent] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  
  // Totals
  const [totals, setTotals] = useState({
    total: 0,
    pending: 0,
    scheduled: 0,
    paid: 0
  });

  // Pay form
  const [payForm, setPayForm] = useState({
    scheduled_date: "",
    notes: ""
  });
  const [paymentFile, setPaymentFile] = useState(null);

  const fetchCommissions = useCallback(async () => {
    try {
      setLoading(true);
      const [commissionsRes, agentsRes] = await Promise.all([
        axios.get(`${API}/admin/commissions`, axCfg),
        axios.get(`${API}/admin/agents`, axCfg)
      ]);
      
      let data = commissionsRes.data;
      
      // Apply filters
      if (filterAgent) {
        data = data.filter(c => c.agent_id === filterAgent);
      }
      if (filterStatus) {
        data = data.filter(c => c.status === filterStatus);
      }
      if (filterMonth) {
        data = data.filter(c => {
          const created = c.created_at || "";
          return created.startsWith(filterMonth);
        });
      }
      
      setCommissions(data);
      setAgents(agentsRes.data);
      
      // Calculate totals from ALL commissions (unfiltered)
      const allCommissions = commissionsRes.data;
      setTotals({
        total: allCommissions.reduce((sum, c) => sum + (c.amount || 0), 0),
        pending: allCommissions.filter(c => c.status === "pending").reduce((sum, c) => sum + (c.amount || 0), 0),
        scheduled: allCommissions.filter(c => c.status === "scheduled").reduce((sum, c) => sum + (c.amount || 0), 0),
        paid: allCommissions.filter(c => c.status === "paid").reduce((sum, c) => sum + (c.amount || 0), 0)
      });
      
    } catch (e) {
      console.error("Error loading commissions:", e);
      toast.error("Error al cargar comisiones");
    } finally {
      setLoading(false);
    }
  }, [filterAgent, filterStatus, filterMonth]);

  useEffect(() => {
    fetchCommissions();
  }, [fetchCommissions]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const openDetailModal = (commission) => {
    setSelectedCommission(commission);
    setShowDetailModal(true);
  };

  const openPayModal = (commission) => {
    setSelectedCommission(commission);
    setPayForm({ scheduled_date: "", notes: "" });
    setPaymentFile(null);
    setShowPayModal(true);
  };

  const updateCommissionStatus = async (status) => {
    if (!selectedCommission) return;
    
    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append("status", status);
      
      if (status === "scheduled" && payForm.scheduled_date) {
        formData.append("scheduled_date", payForm.scheduled_date);
      }
      if (payForm.notes) {
        formData.append("notes", payForm.notes);
      }
      if (paymentFile && status === "paid") {
        formData.append("payment_proof", paymentFile);
      }
      
      await axios.patch(
        `${API}/admin/commissions/${selectedCommission.id}/update-status`,
        formData,
        { ...axCfg, headers: { "Content-Type": "multipart/form-data" } }
      );
      
      toast.success(status === "paid" ? "Comisión marcada como pagada" : "Estado actualizado");
      setShowPayModal(false);
      setShowDetailModal(false);
      fetchCommissions();
      
    } catch (e) {
      toast.error("Error al actualizar comisión");
    } finally {
      setUploading(false);
    }
  };

  const exportToExcel = async () => {
    try {
      toast.info("Generando archivo Excel...");
      
      let url = `${API}/admin/commissions/export`;
      const params = new URLSearchParams();
      if (filterAgent) params.append("agent_id", filterAgent);
      if (filterMonth) {
        const [year, month] = filterMonth.split("-");
        params.append("year", year);
        params.append("month", month);
      }
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        ...axCfg,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `comisiones_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      
      toast.success("Archivo Excel descargado");
    } catch (e) {
      toast.error("Error al exportar");
    }
  };

  const clearFilters = () => {
    setFilterAgent("");
    setFilterStatus("");
    setFilterMonth("");
  };

  // Group commissions by agent
  const commissionsByAgent = commissions.reduce((acc, c) => {
    const key = c.agent_id || "unknown";
    if (!acc[key]) {
      acc[key] = {
        agent: agents.find(a => a.id === c.agent_id) || { name: c.agent_name, email: c.agent_email },
        commissions: [],
        total: 0,
        pending: 0,
        paid: 0
      };
    }
    acc[key].commissions.push(c);
    acc[key].total += c.amount || 0;
    if (c.status === "pending" || c.status === "scheduled") acc[key].pending += c.amount || 0;
    if (c.status === "paid") acc[key].paid += c.amount || 0;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="h-8 w-8 animate-spin text-[#103F38]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-[#103F38]" />
            Gestión de Comisiones
          </h2>
          <p className="text-gray-500">Control de pagos a agentes por trámites aprobados</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            onClick={exportToExcel}
            className="text-green-700 border-green-300 hover:bg-green-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar Excel
          </Button>
          <Button 
            variant="outline" 
            onClick={fetchCommissions}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-none shadow-sm bg-gradient-to-br from-gray-50 to-gray-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-500 rounded-lg">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-700">{formatCurrency(totals.total)}</p>
                <p className="text-xs text-gray-500">Total Generado</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-gradient-to-br from-amber-50 to-amber-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500 rounded-lg">
                <Clock className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-lg font-bold text-amber-700">{formatCurrency(totals.pending)}</p>
                <p className="text-xs text-amber-600">Pendiente de Pago</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <CalendarClock className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-lg font-bold text-blue-700">{formatCurrency(totals.scheduled)}</p>
                <p className="text-xs text-blue-600">Programado</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <CheckCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-lg font-bold text-green-700">{formatCurrency(totals.paid)}</p>
                <p className="text-xs text-green-600">Pagado</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-none shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtros:</span>
            </div>
            
            <div className="w-48">
              <Select value={filterAgent || "all"} onValueChange={(v) => setFilterAgent(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Todos los agentes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los agentes</SelectItem>
                  {agents.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-40">
              <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Todos los estados" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="scheduled">Programado</SelectItem>
                  <SelectItem value="paid">Pagada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-40">
              <Input
                type="month"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="h-9"
                placeholder="Mes/Año"
              />
            </div>
            
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Commissions by Agent */}
      {Object.keys(commissionsByAgent).length === 0 ? (
        <Card className="border-none shadow-sm">
          <CardContent className="p-12 text-center">
            <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay comisiones registradas</p>
            <p className="text-sm text-gray-400 mt-1">
              Las comisiones se generan automáticamente cuando un trámite es aprobado
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(commissionsByAgent).map(([agentId, data]) => (
            <Card key={agentId} className="border-none shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#103F38]/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-[#103F38]" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{data.agent?.name || data.agent?.email || "Agente"}</CardTitle>
                      <p className="text-sm text-gray-500">{data.agent?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-right">
                    <div>
                      <p className="text-sm text-gray-500">Total</p>
                      <p className="font-bold text-gray-900">{formatCurrency(data.total)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Pendiente</p>
                      <p className="font-bold text-amber-600">{formatCurrency(data.pending)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Pagado</p>
                      <p className="font-bold text-green-600">{formatCurrency(data.paid)}</p>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {data.commissions.map((commission) => {
                    const statusConfig = STATUS_CONFIG[commission.status] || STATUS_CONFIG.pending;
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <div 
                        key={commission.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        data-testid={`commission-${commission.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${statusConfig.color.split(' ')[0]}`}>
                            <StatusIcon className={`h-4 w-4 ${statusConfig.color.split(' ')[1]}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{commission.tramite_title}</span>
                              <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                              <span className="flex items-center gap-1">
                                <Building2 className="h-3 w-3" />
                                {commission.client_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatDate(commission.created_at)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Percent className="h-3 w-3" />
                                {commission.commission_rate}% de {formatCurrency(commission.base_amount)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-bold text-[#103F38] text-lg">{formatCurrency(commission.amount)}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDetailModal(commission)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {commission.status !== "paid" && (
                              <Button
                                size="sm"
                                className="bg-[#103F38] hover:bg-[#143f34]"
                                onClick={() => openPayModal(commission)}
                              >
                                {commission.status === "pending" ? "Gestionar" : "Pagar"}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-[#103F38]" />
              Detalle de Comisión
            </DialogTitle>
          </DialogHeader>
          
          {selectedCommission && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Estado</Label>
                  <Badge className={STATUS_CONFIG[selectedCommission.status]?.color}>
                    {STATUS_CONFIG[selectedCommission.status]?.label}
                  </Badge>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Monto</Label>
                  <p className="font-bold text-[#103F38] text-xl">{formatCurrency(selectedCommission.amount)}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <Label className="text-xs text-gray-500">Agente</Label>
                <p className="font-medium">{selectedCommission.agent_name || selectedCommission.agent_email}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Cliente</Label>
                  <p className="font-medium">{selectedCommission.client_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Trámite/Expediente</Label>
                  <p className="font-medium">{selectedCommission.tramite_title}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Monto Base</Label>
                  <p className="font-medium">{formatCurrency(selectedCommission.base_amount)}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Porcentaje</Label>
                  <p className="font-medium">{selectedCommission.commission_rate}%</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Fecha de Generación</Label>
                  <p className="font-medium">{formatDate(selectedCommission.created_at)}</p>
                </div>
                {selectedCommission.paid_at && (
                  <div>
                    <Label className="text-xs text-gray-500">Fecha de Pago</Label>
                    <p className="font-medium">{formatDate(selectedCommission.paid_at)}</p>
                  </div>
                )}
              </div>
              
              {selectedCommission.scheduled_date && (
                <div>
                  <Label className="text-xs text-gray-500">Fecha Programada de Pago</Label>
                  <p className="font-medium">{formatDate(selectedCommission.scheduled_date)}</p>
                </div>
              )}
              
              {selectedCommission.payment_proof_filename && (
                <div className="bg-green-50 p-3 rounded-lg">
                  <Label className="text-xs text-green-700">Comprobante de Pago</Label>
                  <p className="font-medium text-green-800 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    {selectedCommission.payment_proof_filename}
                  </p>
                </div>
              )}
              
              {selectedCommission.notes && (
                <div>
                  <Label className="text-xs text-gray-500">Notas</Label>
                  <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">{selectedCommission.notes}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Cerrar
            </Button>
            {selectedCommission?.status !== "paid" && (
              <Button 
                className="bg-[#103F38] hover:bg-[#143f34]"
                onClick={() => {
                  setShowDetailModal(false);
                  openPayModal(selectedCommission);
                }}
              >
                Gestionar Pago
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay/Schedule Modal */}
      <Dialog open={showPayModal} onOpenChange={setShowPayModal}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-[#103F38]" />
              Gestionar Pago de Comisión
            </DialogTitle>
            <DialogDescription>
              {formatCurrency(selectedCommission?.amount)} para {selectedCommission?.agent_name || selectedCommission?.agent_email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Trámite:</strong> {selectedCommission?.tramite_title}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Cliente:</strong> {selectedCommission?.client_name}
              </p>
            </div>
            
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <CalendarClock className="h-4 w-4" />
                Programar Pago
              </div>
              <div>
                <Label>Fecha de Pago Programada</Label>
                <Input
                  type="date"
                  value={payForm.scheduled_date}
                  onChange={(e) => setPayForm({ ...payForm, scheduled_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <Button 
                variant="outline"
                className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={() => updateCommissionStatus("scheduled")}
                disabled={uploading || !payForm.scheduled_date}
              >
                <CalendarClock className="h-4 w-4 mr-2" />
                Marcar como Programado
              </Button>
            </div>
            
            <div className="border rounded-lg p-4 space-y-4 border-green-200 bg-green-50/50">
              <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                <CheckCircle className="h-4 w-4" />
                Marcar como Pagada
              </div>
              <div>
                <Label>Comprobante de Transferencia *</Label>
                <Input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => setPaymentFile(e.target.files?.[0] || null)}
                  className="mt-1"
                />
                {paymentFile && (
                  <p className="text-xs text-green-600 mt-1">
                    Archivo: {paymentFile.name}
                  </p>
                )}
              </div>
              <div>
                <Label>Notas (opcional)</Label>
                <Textarea
                  placeholder="Referencia de transferencia, banco, etc."
                  value={payForm.notes}
                  onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                  className="mt-1"
                  rows={2}
                />
              </div>
              <Button 
                className="w-full bg-green-600 hover:bg-green-700"
                onClick={() => updateCommissionStatus("paid")}
                disabled={uploading || !paymentFile}
              >
                {uploading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Confirmar Pago
                  </>
                )}
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayModal(false)}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
